import {
  BookmarkMinusIcon,
  BanIcon,
  BellOffIcon,
  HashIcon,
  KeyRoundIcon,
  Link2OffIcon,
  LogOutIcon,
  MergeIcon,
  ShieldOffIcon,
  SmileIcon,
  Trash2Icon,
  UnplugIcon,
  UserMinusIcon,
  UserXIcon,
  type LucideIcon,
} from "lucide-react"
import type { ReactNode } from "react"
import { ConfirmationShell, type ConfirmationTrigger } from "./confirmation.tsx"
import { wordingOf, type Removal } from "./destructive-confirmation.ts"

export type { Removal } from "./destructive-confirmation.ts"

/** Each case's icon: a bin for what is deleted, and a sign of what is undone for the rest. */
const icons = {
  item: Trash2Icon,
  feed: Trash2Icon,
  ticket: Trash2Icon,
  epic: Trash2Icon,
  attachment: Trash2Icon,
  comment: Trash2Icon,
  "chat-message": Trash2Icon,
  group: Trash2Icon,
  status: Trash2Icon,
  area: Trash2Icon,
  label: Trash2Icon,
  "group-membership": UserMinusIcon,
  assignee: UserMinusIcon,
  "admin-role": ShieldOffIcon,
  "member-access": UserXIcon,
  "duplicate-member": MergeIcon,
  "connector-account": UnplugIcon,
  "connector-consent": UnplugIcon,
  "connected-agent": UnplugIcon,
  "access-token": KeyRoundIcon,
  identity: Trash2Icon,
  "project-grant": KeyRoundIcon,
  "folder-grant": KeyRoundIcon,
  relation: Link2OffIcon,
  "channel-rule": HashIcon,
  budget: BellOffIcon,
  "linked-repo": Link2OffIcon,
  "linked-pr": Link2OffIcon,
  "unsaved-text": Trash2Icon,
  "change-request": BanIcon,
  "chat-participant": UserMinusIcon,
  "private-channel-membership": LogOutIcon,
  "group-dm-membership": LogOutIcon,
  "custom-emoji": SmileIcon,
  "chat-bookmark": BookmarkMinusIcon,
  "shared-view": BookmarkMinusIcon,
  company: Trash2Icon,
  product: Trash2Icon,
  "generation-drafts": Trash2Icon,
} satisfies Record<Removal["removes"], LucideIcon>

/*
 * The one way the web app asks before it takes something away: a trigger, and the confirmation it
 * opens, worded for the removal from the closed list in `destructive-confirmation.ts`.
 *
 * `trigger` is the only choice about looks: a labelled quiet button that turns red on hover, a red
 * item at the end of a row's menu, or a row's muted × whose accessible name still says what it
 * removes. `choice` is a question the removal can't go ahead without, such as where a removed
 * Status's tickets go, asked beneath the consequence.
 */
export function DestructiveConfirmation({
  trigger,
  onConfirm,
  choice,
  open,
  onOpenChange,
  navigationBlocker,
  ...removal
}: Removal & {
  trigger: ConfirmationTrigger
  onConfirm: () => Promise<unknown>
  choice?: ReactNode
  /** With `trigger="none"`: whether it is open, as the page that opens it says. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /**
   * With `trigger="none"`: a router's navigation blocker opens it (unsaved text the page would
   * leave), or a sheet beneath it that answers Back itself (the Composer's full screen), so Back
   * is theirs and it pushes no history entry of its own on `compact`.
   */
  navigationBlocker?: boolean
}) {
  return (
    <ConfirmationShell
      tone="destructive"
      icon={icons[removal.removes]}
      wording={wordingOf(removal)}
      trigger={trigger}
      onConfirm={onConfirm}
      choice={choice}
      {...(open !== undefined && { open })}
      {...(onOpenChange && { onOpenChange })}
      {...(navigationBlocker && { navigationBlocker })}
    />
  )
}
